// Luminance-aware brand palette derivation for the Locker portal.
//
// The portal brands itself from a single owner-chosen accent color (`Agency.BrandColor`).
// This derives a coordinated, contrast-safe set of tokens so the page reads well whether the
// agency picks pale yellow, neon, or near-black — the page side never trusts the raw color
// blindly (see ADR-0001: branding is color-only, the look is manufactured here).

export type BrandPalette = {
  /** The raw brand color, normalized to #rrggbb. */
  base: string;
  /** Readable text/icon color sitting directly on `base` (white or near-black). */
  onBase: string;
  /** A darker shade of the hue — hover, borders, emphasis. */
  dark: string;
  /** Color safe to use as a filled button: `base`, auto-darkened if `base` is too light. */
  interactive: string;
  /** Readable text/icon on `interactive`. */
  onInteractive: string;
  /** Very light wash of the hue for surfaces/backgrounds. */
  tint: string;
  /** Slightly deeper tint for hairline borders on light surfaces. */
  tintBorder: string;
  /** Gradient stops for hero backgrounds (lighter → darker variant of the hue). */
  gradientFrom: string;
  gradientTo: string;
  /** True when the base color was too light to use as a white-text button. */
  baseIsLight: boolean;
};

type Rgb = { r: number; g: number; b: number };

const NEAR_BLACK = '#161616';
const WHITE = '#ffffff';

/** Default Miami Ship accent, used when the agency has no brand color. */
export const DEFAULT_BRAND_COLOR = '#1E7A3F';

function clamp(n: number) {
  return Math.max(0, Math.min(255, Math.round(n)));
}

function hexToRgb(hex: string): Rgb {
  let h = hex.replace('#', '').trim();
  if (h.length === 3) {
    h = h
      .split('')
      .map((c) => c + c)
      .join('');
  }
  return {
    r: parseInt(h.slice(0, 2), 16),
    g: parseInt(h.slice(2, 4), 16),
    b: parseInt(h.slice(4, 6), 16),
  };
}

function rgbToHex({ r, g, b }: Rgb): string {
  const toHex = (n: number) => clamp(n).toString(16).padStart(2, '0');
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}

// t = 0 → a, t = 1 → b
function mix(a: Rgb, b: Rgb, t: number): Rgb {
  return {
    r: a.r + (b.r - a.r) * t,
    g: a.g + (b.g - a.g) * t,
    b: a.b + (b.b - a.b) * t,
  };
}

function srgbToLinear(channel: number) {
  const c = channel / 255;
  return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
}

function relativeLuminance({ r, g, b }: Rgb) {
  return 0.2126 * srgbToLinear(r) + 0.7152 * srgbToLinear(g) + 0.0722 * srgbToLinear(b);
}

function contrastRatio(a: Rgb, b: Rgb) {
  const la = relativeLuminance(a);
  const lb = relativeLuminance(b);
  const [hi, lo] = la > lb ? [la, lb] : [lb, la];
  return (hi + 0.05) / (lo + 0.05);
}

const white: Rgb = { r: 255, g: 255, b: 255 };
const black: Rgb = hexToRgb(NEAR_BLACK);

/** Pick whichever of white / near-black reads better on `surface`. */
function readableOn(surface: Rgb): string {
  return contrastRatio(white, surface) >= contrastRatio(black, surface) ? WHITE : NEAR_BLACK;
}

const HEX_RE = /^#?([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/;

export function isValidHex(value?: string | null): value is string {
  return typeof value === 'string' && HEX_RE.test(value.trim());
}

export function deriveBrandPalette(inputHex?: string | null): BrandPalette {
  const source = isValidHex(inputHex) ? (inputHex as string) : DEFAULT_BRAND_COLOR;
  const base = hexToRgb(source);

  // A filled button with white text needs ~4.5:1. If the brand color is too light for that,
  // darken it (mix toward black) until it clears the bar — keeping the hue, losing the glare.
  const whiteContrast = contrastRatio(white, base);
  const baseIsLight = whiteContrast < 4.5;

  let interactiveRgb = base;
  let guard = 0;
  while (contrastRatio(white, interactiveRgb) < 4.5 && guard < 24) {
    interactiveRgb = mix(interactiveRgb, black, 0.08);
    guard += 1;
  }

  return {
    base: rgbToHex(base),
    onBase: readableOn(base),
    dark: rgbToHex(mix(base, black, 0.28)),
    interactive: rgbToHex(interactiveRgb),
    onInteractive: readableOn(interactiveRgb),
    tint: rgbToHex(mix(base, white, 0.92)),
    tintBorder: rgbToHex(mix(base, white, 0.76)),
    gradientFrom: rgbToHex(mix(base, white, 0.12)),
    gradientTo: rgbToHex(mix(base, black, 0.32)),
    baseIsLight,
  };
}
